import { useMemo, useState } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ExternalLink,
  FileSignature,
  Info,
  Loader2,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import {
  useEquipmentAssets,
  useEquipmentContracts,
  type EquipmentAssetRow,
  type EquipmentContractRow,
} from '@/hooks/useEquipmentRentals';
import { fmtMirrorDate } from '@/lib/dates';

/**
 * EquipmentSection — full leased-equipment view on /account. Reads
 * orders.v_equipment_assets + orders.v_equipment_contracts, displays a
 * summary header + an expandable list of assets grouped by contract.
 *
 * Self-hides when the customer has no assets in the mirror. (The
 * cross-Supabase sync also short-circuits for that case, so the section
 * stays clean for syrup-only customers.)
 */
export function EquipmentSection() {
  const { data: assets, isLoading: assetsLoading } = useEquipmentAssets();
  const { data: contracts } = useEquipmentContracts();
  const [openContract, setOpenContract] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const byContract = new Map<
      string | 'unassigned',
      { contract: EquipmentContractRow | null; assets: EquipmentAssetRow[] }
    >();
    const contractById = new Map<string, EquipmentContractRow>(
      (contracts ?? []).map((c) => [c.id, c]),
    );
    for (const a of assets ?? []) {
      const key = a.contractId ?? 'unassigned';
      const slot =
        byContract.get(key) ?? {
          contract: a.contractId ? (contractById.get(a.contractId) ?? null) : null,
          assets: [] as EquipmentAssetRow[],
        };
      slot.assets.push(a);
      byContract.set(key, slot);
    }
    return Array.from(byContract.values()).sort((x, y) => {
      const xDate = x.contract?.startDate ?? '';
      const yDate = y.contract?.startDate ?? '';
      return yDate.localeCompare(xDate);
    });
  }, [assets, contracts]);

  const totalAssets = (assets ?? []).filter((a) => !a.isLoaner).length;
  const totalMonthly = (assets ?? [])
    .filter((a) => !a.isLoaner)
    .reduce((sum, a) => sum + a.monthlyRent, 0);
  const loanerCount = (assets ?? []).filter((a) => a.isLoaner).length;

  if (!assetsLoading && (assets?.length ?? 0) === 0) return null;

  return (
    <section
      id="equipment"
      className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep"
    >
      <div className="bg-brix-primary-deep px-6 md:px-8 py-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
            <Wrench size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl font-bold tracking-tight text-white leading-none">
              Equipment on rent
            </h2>
            <p className="text-sm text-white/85 tracking-tight leading-snug mt-1.5">
              Live list of every dispenser, ice machine, or piece of
              service equipment Brix Beverage is renting to you. Open
              the full page for photos, contracts, and service tickets.
            </p>
          </div>
        </div>
        <Link
          to="/equipment"
          className="shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-white font-semibold text-xs tracking-tight hover:bg-white/25 transition-colors"
        >
          Open <ArrowRight size={12} />
        </Link>
      </div>
      <div className="px-6 md:px-8 pt-6 pb-6">

        {assetsLoading ? (
          <div className="text-base text-slate-900 tracking-tight inline-flex items-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Loading equipment…
          </div>
        ) : (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
              <Stat label="Units on rent" value={String(totalAssets)} />
              <Stat
                label="Monthly rent total"
                value={formatUsd(totalMonthly)}
                tone="primary"
              />
              {loanerCount > 0 && (
                <Stat label="On loan (no charge)" value={String(loanerCount)} />
              )}
            </div>

            {/* Per-contract groups */}
            <div className="space-y-3">
              {grouped.map((g, i) => (
                <ContractGroup
                  key={g.contract?.id ?? `unassigned-${i}`}
                  contract={g.contract}
                  assets={g.assets}
                  expanded={openContract === (g.contract?.id ?? `unassigned-${i}`)}
                  onToggle={() =>
                    setOpenContract((cur) =>
                      cur === (g.contract?.id ?? `unassigned-${i}`)
                        ? null
                        : g.contract?.id ?? `unassigned-${i}`,
                    )
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'primary';
}) {
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface-alt/40 px-3 py-3">
      <div className="text-xs tracking-tight text-slate-900 font-semibold">
        {label}
      </div>
      <div
        className={cn(
          'font-mono text-2xl font-bold tracking-tight leading-none mt-1',
          tone === 'primary' ? 'text-brix-primary' : 'text-slate-900',
        )}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </div>
  );
}

function ContractGroup({
  contract,
  assets,
  expanded,
  onToggle,
}: {
  contract: EquipmentContractRow | null;
  assets: EquipmentAssetRow[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const monthlyEquipment = contract?.monthlyEquipmentAmount ?? 0;
  const monthlyService = contract?.monthlyServiceAmount ?? 0;
  const monthlyTotal = monthlyEquipment + monthlyService;
  const isSigned = contract?.signatureStatus === 'signed';

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left cursor-pointer"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronDown
            size={15}
            className={cn(
              'shrink-0 text-slate-900 transition-transform',
              expanded && 'rotate-180',
            )}
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 tracking-tight truncate">
              {contract?.contractNumber ?? 'Equipment (no contract on file)'}
              {contract?.category && (
                <span className="ml-2 inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-tight bg-brix-primary-tint text-brix-primary-deep">
                  {contract.category}
                </span>
              )}
              {isSigned && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-tight bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <FileSignature size={9} /> Signed
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-900 tracking-tight">
              {assets.length} unit{assets.length === 1 ? '' : 's'}
              {contract?.startDate && ` · started ${fmtDate(contract.startDate)}`}
            </div>
          </div>
        </div>
        {monthlyTotal > 0 && (
          <div
            className="font-mono text-sm font-bold text-slate-900 tracking-tight shrink-0"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatUsd(monthlyTotal)} / mo
          </div>
        )}
      </button>

      {expanded && (
        <div className="border-t border-brix-border bg-brix-surface-alt/30">
          {contract?.externalDocumentUrl && (
            <div className="px-4 py-2 border-b border-brix-border text-xs text-slate-900 tracking-tight">
              <a
                href={contract.externalDocumentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brix-primary font-semibold underline inline-flex items-center gap-1"
              >
                <ExternalLink size={11} /> View signed contract
              </a>
            </div>
          )}
          {assets.length === 0 ? (
            <div className="px-4 py-4 text-sm text-slate-900 tracking-tight italic">
              No assets currently installed under this contract.
            </div>
          ) : (
            <div className="divide-y divide-brix-border">
              {assets.map((a) => (
                <AssetRow key={a.id} asset={a} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AssetRow({ asset }: { asset: EquipmentAssetRow }) {
  const title = asset.qboItemName ?? asset.description ?? asset.assetTag ?? 'Equipment unit';
  const sub = [asset.make, asset.modelNumber].filter(Boolean).join(' · ');

  return (
    <div className="px-4 py-3 flex items-start gap-3 justify-between text-sm">
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 tracking-tight">{title}</div>
        {sub && (
          <div className="text-xs text-slate-900 tracking-tight mt-0.5">{sub}</div>
        )}
        <div className="text-[11px] text-slate-900 tracking-tight mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
          {asset.serialNumber && (
            <span>SN: <span className="font-mono">{asset.serialNumber}</span></span>
          )}
          {asset.assetTag && (
            <span>Tag: <span className="font-mono">{asset.assetTag}</span></span>
          )}
          {asset.installedAt && (
            <span>Installed: {fmtDate(asset.installedAt)}</span>
          )}
        </div>
        {asset.isLoaner && (
          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-tight bg-amber-50 text-amber-800 border border-amber-200">
            <Info size={9} /> Loaner · no charge
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div
          className="font-mono text-sm font-bold text-slate-900 tracking-tight"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {asset.isLoaner ? '—' : `${formatUsd(asset.monthlyRent)} / mo`}
        </div>
        {asset.status && (
          <div className="text-xs tracking-tight text-slate-900 font-semibold mt-0.5">
            {asset.status}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  return fmtMirrorDate(iso);
}
